import React, { useRef, useState } from "react";
import Papa from "papaparse";
import {
  AreaChart,
  Area,
  ResponsiveContainer,
} from "recharts";
import { Button } from "../../../components";

export interface TimestampedEntry {
  timestamp: string;
  value: number;
}

interface Props {
  currentFlatLoad?: number | null;
  currentProfile?: number[] | null;
  onUpload: (profile: number[]) => void;
  /** Optional: when the CSV carries a timestamp column, the profile is
   * self-describing — its timestamps define the analysis period (parity with a
   * generation project's uploaded profile). Falls back to onUpload when absent. */
  onUploadTimestamped?: (entries: TimestampedEntry[]) => void;
  onClearProfile: () => void;
}

const EXPECTED_ROWS = 8760;
const VALID_COLUMN_NAMES = ["load_mw", "value", "load", "mw"];
const TIMESTAMP_COLUMN_NAMES = [
  "timestamp",
  "datetime",
  "date_time",
  "date",
  "time",
  "ts",
  "period",
  "hour_ending",
  "datetime_utc",
];

function detectColumn(headers: string[]): string | null {
  const lower = headers.map((h) => h.toLowerCase().trim());
  for (const name of VALID_COLUMN_NAMES) {
    const idx = lower.indexOf(name);
    if (idx >= 0) return headers[idx];
  }
  // Fall back to first column
  return headers.length > 0 ? headers[0] : null;
}

function detectTimestampColumn(headers: string[]): string | null {
  const lower = headers.map((h) => h.toLowerCase().trim());
  for (const name of TIMESTAMP_COLUMN_NAMES) {
    const idx = lower.indexOf(name);
    if (idx >= 0) return headers[idx];
  }
  return null;
}

function yearLabel(entries: TimestampedEntry[]): string {
  const times = entries
    .map((e) => new Date(e.timestamp).getTime())
    .filter((t) => !isNaN(t));
  if (times.length === 0) return "";
  const min = new Date(Math.min(...times));
  const max = new Date(Math.max(...times));
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return `${fmt(min)} → ${fmt(max)}`;
}

function sparklineData(profile: number[]): { v: number }[] {
  // Downsample to ~365 daily averages for the sparkline
  const result: { v: number }[] = [];
  const step = 24;
  for (let i = 0; i < profile.length; i += step) {
    const chunk = profile.slice(i, i + step);
    const avg = chunk.reduce((a, b) => a + b, 0) / chunk.length;
    result.push({ v: Math.round(avg * 100) / 100 });
  }
  return result;
}

export function LoadProfileUpload({
  currentFlatLoad,
  currentProfile,
  onUpload,
  onUploadTimestamped,
  onClearProfile,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [parsing, setParsing] = useState(false);
  const [period, setPeriod] = useState<string | null>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setPeriod(null);
    setParsing(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setParsing(false);
        const data = results.data as Record<string, string>[];
        const headers = results.meta.fields || [];

        const tsCol =
          onUploadTimestamped ? detectTimestampColumn(headers) : null;
        const col = detectColumn(headers.filter((h) => h !== tsCol));
        if (!col) {
          setError("Could not detect load column. Use headers: load_mw, value, load, or mw.");
          return;
        }

        // TIMESTAMPED path: a timestamp column makes the profile self-describing
        // (its timestamps define the analysis period), so we don't require
        // exactly 8760 rows — a full year or a partial span is accepted.
        if (tsCol) {
          const entries: TimestampedEntry[] = [];
          for (let i = 0; i < data.length; i++) {
            const rawTs = (data[i][tsCol] || "").trim();
            const raw = data[i][col];
            const val = parseFloat(raw);
            if (!rawTs) {
              setError(`Row ${i + 1}: missing timestamp in column "${tsCol}"`);
              return;
            }
            if (isNaN(new Date(rawTs).getTime())) {
              setError(`Row ${i + 1}: unparseable timestamp "${rawTs}"`);
              return;
            }
            if (isNaN(val)) {
              setError(`Row ${i + 1}: invalid number "${raw}" in column "${col}"`);
              return;
            }
            if (val < 0) {
              setError(`Row ${i + 1}: negative value (${val}). All values must be >= 0.`);
              return;
            }
            entries.push({ timestamp: rawTs, value: val });
          }
          if (entries.length === 0) {
            setError("Timestamped profile is empty.");
            return;
          }
          setPeriod(yearLabel(entries));
          onUploadTimestamped!(entries);
          if (fileRef.current) fileRef.current.value = "";
          return;
        }

        // BARE-LIST path (unchanged): positional 8760 values.
        if (data.length !== EXPECTED_ROWS) {
          setError(
            `Expected ${EXPECTED_ROWS} rows, got ${data.length}. CSV must contain exactly 8760 hourly values (or add a timestamp column for a period-derived profile).`
          );
          return;
        }

        const values: number[] = [];
        for (let i = 0; i < data.length; i++) {
          const raw = data[i][col];
          const val = parseFloat(raw);
          if (isNaN(val)) {
            setError(`Row ${i + 1}: invalid number "${raw}" in column "${col}"`);
            return;
          }
          if (val < 0) {
            setError(`Row ${i + 1}: negative value (${val}). All values must be >= 0.`);
            return;
          }
          values.push(val);
        }

        onUpload(values);
        // Reset file input so same file can be re-uploaded
        if (fileRef.current) fileRef.current.value = "";
      },
      error: (err) => {
        setParsing(false);
        setError(`Parse error: ${err.message}`);
      },
    });
  };

  if (currentProfile && currentProfile.length > 0) {
    const min = Math.min(...currentProfile);
    const max = Math.max(...currentProfile);
    const avg = currentProfile.reduce((a, b) => a + b, 0) / currentProfile.length;

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-body-sm text-text-muted">Custom Load Profile</span>
          <Button variant="ghost" size="sm" onClick={onClearProfile}>
            Remove
          </Button>
        </div>
        <div className="bg-surface-600 rounded-lg p-3">
          <ResponsiveContainer width="100%" height={60}>
            <AreaChart data={sparklineData(currentProfile)}>
              <defs>
                <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="v"
                stroke="#06b6d4"
                fill="url(#sparkGrad)"
                strokeWidth={1.5}
                dot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex gap-4 text-body-sm text-text-muted mt-1">
            <span>Min: {min.toFixed(1)} MW</span>
            <span>Avg: {avg.toFixed(1)} MW</span>
            <span>Max: {max.toFixed(1)} MW</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <label className="cursor-pointer">
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            onChange={handleFile}
            className="hidden"
          />
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-600 border border-border rounded-lg text-body-sm text-text-primary hover:bg-surface-500 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {parsing ? "Parsing..." : "Upload 8760 Load Profile (CSV)"}
          </span>
        </label>
        {currentFlatLoad && (
          <span className="text-body-sm text-text-muted">
            Current: {currentFlatLoad} MW flat
          </span>
        )}
      </div>
      {period && (
        <div className="text-body-sm text-cyan-300">
          Timestamped profile — analysis period {period} (derived from
          timestamps; no year picked)
        </div>
      )}
      {error && <div className="text-red-400 text-body-sm">{error}</div>}
    </div>
  );
}
